import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, Plus, X, AlertTriangle, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const INITIAL = [
  { wo: "WO-2026-041", title: "Hydrant Pump P-01 Not Starting",     system: "Fire-Hydrant", priority: "Critical", raisedBy: "Alert (Auto)",   assignee: "Suresh Patil",     status: "Overdue",    raised: "2026-05-01", due: "2026-05-01", desc: "Main hydrant pump tripping on start-up. Auto-raised from CRITICAL alert.", log: [["2026-05-01 09:12","System","WO opened from alert"],["2026-05-01 11:40","Suresh Patil","Site visit scheduled"]] },
  { wo: "WO-2026-042", title: "AHU-04 Filter Choked — IAQ Alert",    system: "HVAC",         priority: "High",     raisedBy: "Alert (Auto)",   assignee: "Anil Sharma",      status: "In Progress", raised: "2026-05-02", due: "2026-05-03", desc: "Food Court PM2.5 alert linked to AHU-04. Filter replacement required.", log: [["2026-05-02 08:02","System","WO opened from alert"],["2026-05-02 10:15","Anil Sharma","Replacement filter ordered"]] },
  { wo: "WO-2026-043", title: "Sprinkler Zone 3 Valve Leaking",      system: "Sprinkler",    priority: "Critical", raisedBy: "Ravi Nair",      assignee: "Vaibhav P.",       status: "Overdue",    raised: "2026-04-30", due: "2026-05-01", desc: "Visual leak noticed during routine inspection. Estimated 2L/hr drip.", log: [["2026-04-30 16:20","Ravi Nair","WO raised"],["2026-05-01 09:00","Vaibhav P.","Parts inventory check done"]] },
  { wo: "WO-2026-044", title: "Food Court Feeder PF Low",            system: "Electrical",   priority: "Medium",   raisedBy: "Alert (Auto)",   assignee: "Electrician Team", status: "In Progress", raised: "2026-05-02", due: "2026-05-05", desc: "Power factor below 0.90 on MDB-03. Capacitor bank inspection needed.", log: [["2026-05-02 13:05","System","WO opened from alert"]] },
  { wo: "WO-2026-045", title: "Parking B1 CO₂ High — Ventilation",   system: "Ventilation",  priority: "High",     raisedBy: "Alert (Auto)",   assignee: "Anil Sharma",      status: "Open",       raised: "2026-05-03", due: "2026-05-04", desc: "CO₂ at 890 ppm in basement. Extract fan operation check required.", log: [["2026-05-03 07:30","System","WO opened from alert"]] },
  { wo: "WO-2026-046", title: "Gate 3 Access Card Reader Offline",   system: "Access",       priority: "Medium",   raisedBy: "Security Team",  assignee: "Ravi Nair",        status: "In Progress", raised: "2026-05-02", due: "2026-05-04", desc: "Reader not responding to cards. Possible controller issue.", log: [["2026-05-02 14:25","Security Team","WO raised"]] },
  { wo: "WO-2026-047", title: "Rooftop Solar Inverter Warning",       system: "Electrical",   priority: "Medium",   raisedBy: "Alert (Auto)",   assignee: "Electrician Team", status: "Open",       raised: "2026-05-03", due: "2026-05-06", desc: "String-2 DC voltage fluctuation reported by inverter.", log: [["2026-05-03 11:02","System","WO opened from alert"]] },
  { wo: "WO-2026-048", title: "Monthly Report — DG Test Run",         system: "Generator",    priority: "Low",      raisedBy: "Scheduler (Auto)", assignee: "Suresh Patil",    status: "Completed",  raised: "2026-05-01", due: "2026-05-03", desc: "Scheduled monthly DG test — 30 min load bank.", log: [["2026-05-01 09:00","Scheduler","WO opened"],["2026-05-02 11:00","Suresh Patil","Test run completed — report attached"]] },
];

const PRIO_TONE = {
  Critical: { bg: "bg-red-500/20",    text: "text-red-200",    dot: "🔴" },
  High:     { bg: "bg-orange-500/20", text: "text-orange-200", dot: "🟠" },
  Medium:   { bg: "bg-amber-500/20",  text: "text-amber-200",  dot: "🟡" },
  Low:      { bg: "bg-sky-500/20",    text: "text-sky-200",    dot: "🔵" },
};

const STATUS_TONE = {
  Open:         "bg-amber-500/15 text-amber-300",
  "In Progress":"bg-sky-500/15 text-sky-300",
  Overdue:      "bg-red-500/15 text-red-300",
  Completed:    "bg-emerald-500/15 text-emerald-300",
};

const SYSTEMS = ["Fire-Hydrant","Sprinkler","HVAC","Electrical","Ventilation","Lifts","Access","Generator","Plumbing","Other"];

function Kpi({ label, value, tone = "blue" }) {
  const c = {
    blue:   "text-sky-300 bg-sky-500/10 border-sky-500/20",
    red:    "text-red-300 bg-red-500/10 border-red-500/20",
    green:  "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    amber:  "text-amber-300 bg-amber-500/10 border-amber-500/20",
    orange: "text-orange-300 bg-orange-500/10 border-orange-500/20",
  }[tone];
  return (
    <div className={`glass rounded-lg p-5 border ${c}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{label}</div>
      <div className="text-4xl font-extrabold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

export default function WorkOrders() {
  const [rows, setRows] = useState(INITIAL);
  const [searchParams, setSearchParams] = useSearchParams();
  const [prioFilter, setPrioFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sysFilter, setSysFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", desc: "", system: "Fire-Hydrant", priority: "Medium", assignee: "", due: "" });

  // Handle ?prefill=... from Alert Management "Raise WO" button
  useEffect(() => {
    const title = searchParams.get("title");
    const sys = searchParams.get("system");
    const prio = searchParams.get("priority");
    if (title || sys || prio) {
      setForm((f) => ({
        ...f,
        title: title || f.title,
        desc: searchParams.get("desc") || `Auto-raised from alert: ${title || ""}`,
        system: sys || f.system,
        priority: (prio && ["Critical","High","Medium","Low"].includes(prio)) ? prio : f.priority,
      }));
      setShowCreate(true);
      // clear params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const filtered = useMemo(() => rows.filter((r) =>
    (prioFilter === "all" || r.priority === prioFilter) &&
    (statusFilter === "all" || r.status === statusFilter) &&
    (sysFilter === "all" || r.system === sysFilter)
  ), [rows, prioFilter, statusFilter, sysFilter]);

  const kpis = useMemo(() => ({
    open:      rows.filter((r) => r.status === "Open").length,
    inProgress:rows.filter((r) => r.status === "In Progress").length,
    today:     rows.filter((r) => r.status === "Completed" && r.due === "2026-05-03").length + 2,
    overdue:   rows.filter((r) => r.status === "Overdue").length,
  }), [rows]);

  const submitWO = () => {
    if (!form.title) return toast.error("Title is required");
    const num = `WO-2026-${String(48 + rows.length).padStart(3, "0")}`;
    const now = new Date().toISOString().slice(0, 10);
    const row = {
      wo: num, title: form.title, system: form.system, priority: form.priority,
      raisedBy: "Current User", assignee: form.assignee || "Unassigned", status: "Open",
      raised: now, due: form.due || now, desc: form.desc,
      log: [[new Date().toISOString().slice(0, 16).replace("T", " "), "Current User", "WO created"]],
    };
    setRows([row, ...rows]);
    setShowCreate(false);
    setForm({ title: "", desc: "", system: "Fire-Hydrant", priority: "Medium", assignee: "", due: "" });
    toast.success(`${num} created`);
  };

  const updateSelected = (patch) => {
    setRows((prev) => prev.map((r) => r.wo === selected.wo ? { ...r, ...patch,
      log: [...r.log, [new Date().toISOString().slice(0, 16).replace("T", " "), "Current User",
        patch.status ? `Status → ${patch.status}` : patch.assignee ? `Reassigned → ${patch.assignee}` : "Updated"]] } : r));
    setSelected((s) => ({ ...s, ...patch }));
  };

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
            <ClipboardList className="w-8 h-8 text-orange-400" strokeWidth={2.5} /> Work Order Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Track maintenance jobs · alert-linked automation</p>
        </div>
        <Button className="f360-gradient border-0" data-testid="create-wo-btn" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Create Work Order
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label="Total Open" value={kpis.open} tone="orange" />
        <Kpi label="In Progress" value={kpis.inProgress} tone="blue" />
        <Kpi label="Completed Today" value={kpis.today} tone="green" />
        <Kpi label="Overdue" value={kpis.overdue} tone="red" />
        <Kpi label="Avg Resolution" value="4.2 hrs" tone="amber" />
      </div>

      {/* FILTERS */}
      <div className="glass rounded-lg p-4 flex items-center gap-3 flex-wrap">
        <span className="text-[11px] uppercase tracking-widest text-gray-500">Filters</span>
        <Select value={prioFilter} onValueChange={setPrioFilter}>
          <SelectTrigger className="w-36 bg-gray-50 border-gray-200"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Priorities</SelectItem>
            {Object.keys(PRIO_TONE).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-gray-50 border-gray-200"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Status</SelectItem>
            {Object.keys(STATUS_TONE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sysFilter} onValueChange={setSysFilter}>
          <SelectTrigger className="w-40 bg-gray-50 border-gray-200"><SelectValue placeholder="System" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Systems</SelectItem>
            {SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* TABLE */}
      <div className="glass rounded-lg overflow-hidden" data-testid="wo-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3">WO Number</th>
                <th className="text-left py-3">Title</th>
                <th className="text-left py-3">System</th>
                <th className="text-center py-3">Priority</th>
                <th className="text-left py-3">Raised By</th>
                <th className="text-left py-3">Assigned</th>
                <th className="text-center py-3">Status</th>
                <th className="text-left py-3">Raised</th>
                <th className="text-left py-3">Due</th>
                <th className="text-right px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const p = PRIO_TONE[r.priority];
                return (
                  <tr key={r.wo} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`wo-row-${r.wo}`}>
                    <td className="px-5 py-3 font-mono text-xs">{r.wo}</td>
                    <td className="py-3 font-semibold max-w-xs truncate">{r.title}</td>
                    <td className="py-3 text-gray-500 text-xs">{r.system}</td>
                    <td className="py-3 text-center"><span className={`px-2 py-0.5 rounded-full ${p.bg} ${p.text} text-[10px] uppercase tracking-widest font-bold`}>{r.priority}</span></td>
                    <td className="py-3 text-xs">{r.raisedBy}</td>
                    <td className="py-3">{r.assignee}</td>
                    <td className="py-3 text-center"><span className={`px-2 py-0.5 rounded-full ${STATUS_TONE[r.status]} text-[10px] uppercase tracking-widest font-bold`}>{r.status}</span></td>
                    <td className="py-3 font-mono text-xs">{new Date(r.raised).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                    <td className="py-3 font-mono text-xs">{new Date(r.due).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setSelected(r)} data-testid={`view-wo-${r.wo}`} className="text-orange-300 hover:text-gray-900 text-xs inline-flex items-center gap-1">View <ArrowUpRight className="w-3 h-3" strokeWidth={2.5} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* DETAIL SLIDE PANEL */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative ml-auto w-full max-w-lg h-full bg-white border-l border-gray-200 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wo-detail-panel">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{selected.wo}</div>
                <h2 className="text-xl font-bold">{selected.title}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-900"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full ${PRIO_TONE[selected.priority].bg} ${PRIO_TONE[selected.priority].text} text-[10px] uppercase tracking-widest font-bold`}>{selected.priority}</span>
                <span className={`px-2 py-0.5 rounded-full ${STATUS_TONE[selected.status]} text-[10px] uppercase tracking-widest font-bold`}>{selected.status}</span>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Description</div>
                <p className="text-sm text-gray-600">{selected.desc}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">System</div><div className="font-semibold">{selected.system}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Raised By</div><div className="font-semibold">{selected.raisedBy}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Raised</div><div className="font-mono text-xs">{selected.raised}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Due By</div><div className="font-mono text-xs">{selected.due}</div></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Status</div>
                  <Select value={selected.status} onValueChange={(v) => updateSelected({ status: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="wo-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">{Object.keys(STATUS_TONE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Assigned To</div>
                  <Input value={selected.assignee} onChange={(e) => setSelected({ ...selected, assignee: e.target.value })} onBlur={() => updateSelected({ assignee: selected.assignee })} className="bg-gray-50 border-gray-200" />
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Activity Timeline</div>
                <div className="space-y-2 text-xs">
                  {selected.log.map(([ts, user, action], idx) => (
                    <div key={idx} className="flex gap-3 bg-gray-50 p-2.5 rounded-lg">
                      <Clock className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" strokeWidth={2.5} />
                      <div className="flex-1">
                        <div className="font-mono text-[10px] text-gray-500">{ts}</div>
                        <div className="text-slate-200"><b>{user}:</b> {action}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t border-gray-200">
                <Button className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30" onClick={() => { updateSelected({ status: "Completed" }); toast.success("WO closed"); }} data-testid="close-wo-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Close Work Order
                </Button>
                <Button variant="outline" className="flex-1 border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => { updateSelected({ priority: "Critical" }); toast("Escalated"); }} data-testid="escalate-wo-btn">
                  <AlertTriangle className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Escalate
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CREATE SLIDE PANEL */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setShowCreate(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative ml-auto w-full max-w-md h-full bg-white border-l border-gray-200 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-wo-panel">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-bold">Create Work Order</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-500 hover:text-gray-900"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Title</span>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="wo-form-title" /></label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Description</span>
                <Textarea value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className="bg-gray-50 border-gray-200" rows={3} /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">System</span>
                  <Select value={form.system} onValueChange={(v) => setForm({ ...form, system: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">{SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></label>
                <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Priority</span>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">{Object.keys(PRIO_TONE).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></label>
              </div>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Assign To</span>
                <Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="bg-gray-50 border-gray-200" placeholder="e.g. Suresh Patil" /></label>
              <label className="flex flex-col gap-1 text-sm"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Due Date</span>
                <Input type="date" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} className="bg-gray-50 border-gray-200" /></label>
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Attach Photo</span>
                <div className="bg-gray-50 border border-dashed border-white/15 rounded-lg p-4 text-center text-xs text-gray-500">📎 Drop file here · upload coming soon</div>
              </div>
              <div className="pt-2 flex gap-2">
                <Button className="flex-1 f360-gradient border-0" onClick={submitWO} data-testid="submit-wo-btn">Submit Work Order</Button>
                <Button variant="outline" className="border-white/15" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
